import * as Sentry from '@sentry/browser';

import { CancellationError, RequestError } from '../../shared/lib/request';
import { IParams } from '../types/node.types';

const IGNORED_ERRORS = [
  'Load failed',
  'Failed to fetch',
  'Unexpected end of JSON input',
  'NetworkError when attempting to fetch resource',
  'The string did not match the expected pattern',
  "Failed to execute 'json'",
  'The operation was aborted',
  'JSON.parse',
  'cancelled',
  'отменено',
  'annullato',
  'SecurityError',
].map((error) => error.toLowerCase());

export type SentryClient = ReturnType<typeof initTelemetry>;

/**
 * Берет инстанс ошибки и отвечает на вопрос можно ли ее скипнуть, на основании того,
 * что в мессадже этой ошибки есть строка из нашего блэклиста
 */
function isCommonIgnorableError(exception: unknown): boolean {
  if (exception instanceof Error) {
    const errorMessage = exception.message.toLowerCase();

    // eslint-disable-next-line
    for (const ignoredError of IGNORED_ERRORS) {
      if (errorMessage.includes(ignoredError)) {
        return true;
      }
    }
  }

  return false;
}

function isSecurityError(exception: unknown): boolean {
  if (
    exception instanceof DOMException &&
    exception instanceof CancellationError &&
    exception instanceof RequestError &&
    (exception.name === 'SecurityError' ||
      exception.name === 'AbortError' ||
      exception.name === 'NetworkError')
  ) {
    return true;
  }

  if (
    exception &&
    typeof exception === 'object' &&
    'name' in exception &&
    // @ts-expect-error have a name
    exception.name === 'SecurityError'
  ) {
    return true;
  }

  return false;
}

export function initTelemetry(currentScriptSrc: string, params: Partial<IParams>) {
  const sentry = Sentry.init({
    dsn: 'https://1c30377dafc708e9dfdf6df7e355a97c@sentry.avs.io/20',
    environment: process.env.NODE_ENV,
    release: process.env.NODE_ENV === 'production' ? process.env.REVISION.commithash : '',
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 0.1,
    tracesSampleRate: 0.1,
    maxBreadcrumbs: 20,
    initialScope: {
      tags: { app: 'cascoon' },
      contexts: { widget: params },
    },
    integrations: [
      Sentry.thirdPartyErrorFilterIntegration({
        filterKeys: ['cascoon'],
        behaviour: 'drop-error-if-contains-third-party-frames',
      }),
    ],
    ignoreErrors: [
      'SecurityError', // Чтобы точно не попадали SecurityError, возможно строчки
      /SecurityError/i, // ниже достаточно, но я обмазал все по-максимуму.
      'An attempt was made to break through the security policy of the user agent.',
    ],

    // src https://github.com/KosyanMedia/selene/blob/dbe14e4eb6791129d3e26d4a35cbe09316ebce88/client/src/internal/client_logger.ts#L67
    beforeBreadcrumb(breadcrumb) {
      const MAX_BREADCRUMB_STR_LENGTH = 120;

      /**
       * В бредкрамбсы попадают, например, ебейшие длинные урлы запросов во всякие яндекс-метрики и прочие аналитики
       *
       * Из за этого размеры ивентов становятся довольно неприличными и их может отбрасывать Сентри-сервер
       *
       * Поэтому обрезаем длинные строки, чтобы не попадать в этот лимит - но и не терять информацию
       */
      if (breadcrumb?.data?.url) {
        if (breadcrumb.data.url.length > MAX_BREADCRUMB_STR_LENGTH) {
          // eslint-disable-next-line
          breadcrumb.data.url = `${breadcrumb.data.url.slice(0, MAX_BREADCRUMB_STR_LENGTH)}...`;
        }
      }

      return breadcrumb;
    },

    beforeSend(event, hint) {
      const { hostname } = window.location;

      if (hostname === '' || hostname === 'localhost' || hostname === '127.0.0.1') {
        return null;
      }

      const { originalException } = hint;

      if (isCommonIgnorableError(originalException) || isSecurityError(originalException)) {
        return null;
      }

      if (event.exception) {
        let currentScriptHost = '';

        try {
          currentScriptHost = new URL(currentScriptSrc).host;
        } catch {
          // не смогли определить чья ошибка, так что скипаем
          return null;
        }

        const frames = event.exception.values?.[0]?.stacktrace?.frames || [];

        if (!frames.length) {
          return null;
        }

        const isOwnScript = frames.some(
          (frame) => frame.filename && frame.filename.includes(currentScriptHost),
        );

        if (!isOwnScript) {
          return null;
        }
      }

      return event;
    },
  });

  return sentry;
}
